import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { Badge, Card, CardHeader, Detail, ErrorState, LoadingBlock, PageHeader } from '../components/ui';
import { useAuth } from '../lib/auth';
import { OUTCOME_LABEL, OUTCOME_TONE, PARTICIPANT_LABEL, callClock, callsApi, endedText, listSeparator } from '../lib/calls';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime } from '../lib/format';
import { clientPersonName, companyName } from '../lib/people';

/** One of the client's calls: whom it was with, who of the client made it, how it went and how long. */
export function CallDetailPage() {
  const { id } = useParams();
  const { t, locale, localized } = useI18n();
  const { account } = useAuth();
  const { data: call, isLoading, error, refetch } = useQuery({
    queryKey: ['calls', 'detail', id],
    queryFn: () => callsApi.detail(id!),
    enabled: !!id,
    // Still going on: looked at again until it is over.
    refetchInterval: (q) => (q.state.data?.outcome === 'LIVE' ? 5_000 : false),
  });
  if (isLoading) return <LoadingBlock />;
  if (error || !call) return <ErrorState error={error ?? new Error(t('calls.notFound'))} onRetry={() => void refetch()} />;

  const company = companyName(account, locale);
  const title = call.people.map((p) => localized(p)).join(listSeparator(locale)) || t('calls.title');
  return (
    <>
      <div className="mb-4">
        <Link to="/calls" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('calls.title')}
        </Link>
      </div>
      <PageHeader
        title={title}
        subtitle={call.type === 'GROUP' ? t('calls.groupCall') : t('calls.voiceCall')}
        action={<Badge tone={OUTCOME_TONE[call.outcome]}>{t(OUTCOME_LABEL[call.outcome])}</Badge>}
      />
      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="min-w-0 lg:col-span-2">
          <CardHeader title={t('calls.people')} subtitle={t('calls.peopleHint')} />
          <ul className="divide-y hairline">
            {call.people.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-2.5">
                <span className="min-w-0">
                  <span className="block truncate text-body-sm text-[var(--text-primary)]">{localized(p)}</span>
                  {p.jobTitle && <span className="block truncate text-caption text-[var(--text-secondary)]">{p.jobTitle}</span>}
                </span>
                <Badge tone={p.joined ? 'success' : 'neutral'}>
                  {p.joined ? t('calls.wasOnCall') : t(PARTICIPANT_LABEL[p.status])}
                </Badge>
              </li>
            ))}
          </ul>
          {call.startedAt && (
            <p className="mt-4 flex items-center gap-2 text-caption font-medium text-error-fg dark:text-error-dark">
              <span className="h-2 w-2 shrink-0 rounded-full bg-error" aria-hidden />
              {t('calls.wasRecorded')}
            </p>
          )}
        </Card>
        <Card>
          <CardHeader title={t('calls.details')} />
          <dl>
            <Detail label={t('calls.status')}>{t(`calls.callStatus.${call.status}` as TranslationKey)}</Detail>
            <Detail label={t('calls.madeBy')}>{call.madeBy ? clientPersonName(call.madeBy, company, t('calls.you')) : company}</Detail>
            <Detail label={t('calls.madeAt')}>{formatDateTime(call.createdAt, locale)}</Detail>
            <Detail label={t('calls.started')}>{call.startedAt ? formatDateTime(call.startedAt, locale) : '—'}</Detail>
            <Detail label={t('calls.ended')}>{call.endedAt ? formatDateTime(call.endedAt, locale) : '—'}</Detail>
            <Detail label={t('calls.duration')}>{call.durationSeconds !== null ? callClock(call.durationSeconds) : '—'}</Detail>
            {(call.status === 'FAILED' || call.endedReason === 'MAX_DURATION') && (
              <Detail label={t('calls.why')}>{t(endedText(call.failureCode ?? call.endedReason))}</Detail>
            )}
          </dl>
        </Card>
      </div>
    </>
  );
}
